import { useContext, useEffect, useRef, useState } from 'react';

import { media } from 'context';
import TextButton from 'ds/TextButton';
import { GRAY_50_HEX } from 'shared';

import DateFilter from '../DateFilter';
import FilterDropdown from '../FilterDropdown';
import OneOfOrNotOneOfFilter from '../OneOfOrNotOneOfFilter';
import { HEADER_INPUT_SIZE } from '../constants';
import { Column, FilterState, Row } from '../types';
import Icon from '../../Icon';
import Layout from '../../Layout';
import Modal from '../../Modal';
import Text from '../../Text';
import TextInput from '../../inputs/TextInput';

interface TableControlsHeaderProps<T extends Row> {
  disableSearch?: boolean;
  columns: Column<T>[];
  rows: T[];
  filterState: FilterState[];
  sortState: any[];
  setFilterState: (filters: FilterState[]) => void;
  width?: number;
  clearFilters?: boolean;
}

function TableControlsHeader<T extends Row>({
  disableSearch = false,
  columns,
  filterState,
  setFilterState,
  width,
  clearFilters
}: TableControlsHeaderProps<T>) {
  const { isMobile } = useContext(media);
  const existingTextSearch = filterState.find(filter => filter.key === 'textSearch');
  const [searchValue, setSearchValue] = useState(existingTextSearch?.substring || '');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const modalContainerRef = useRef<HTMLDivElement>(null);

  const columnNamesWithTextSearch = columns
    .filter(column => column.filters?.textSearch?.enabled)
    .map(column => column.header.toLowerCase());

  const allButLast = columnNamesWithTextSearch.slice(0, -1);
  const last = columnNamesWithTextSearch[columnNamesWithTextSearch.length - 1];

  const hasMultipleSearchColumns = columnNamesWithTextSearch.length > 1;
  const searchColumnsText = columnNamesWithTextSearch.length
    ? hasMultipleSearchColumns
      ? `${allButLast.join(', ')} or ${last}`
      : last
    : '';
  const searchLabelColumnsText = searchColumnsText;
  const searchPlaceholderColumnsText = searchColumnsText;
  const searchLabelText = searchLabelColumnsText ? `Search by ${searchLabelColumnsText}` : '';
  const searchPlaceholderText = searchPlaceholderColumnsText ? `Search by ${searchPlaceholderColumnsText}` : '';

  const [searchInputWidth, setSearchInputWidth] = useState(275);

  useEffect(() => {
    if (!searchPlaceholderText) {
      setSearchInputWidth(275);
      return;
    }

    if (typeof document === 'undefined') {
      setSearchInputWidth(275);
      return;
    }

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      setSearchInputWidth(275);
      return;
    }

    context.font = '14px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    const metrics = context.measureText(searchPlaceholderText);
    const calculatedWidth = metrics.width + 48;

    setSearchInputWidth(Math.max(275, Math.ceil(calculatedWidth)));
  }, [searchPlaceholderText]);

  // Handle search input changes: update a global textSearch filter
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    const updated = filterState.filter(f => f.key !== 'textSearch');
    if (value && value.trim().length > 0) {
      // derive searchable column keys from columns that have textSearch enabled
      const searchableKeys = columns.filter(c => c.filters?.textSearch?.enabled).map(c => String(c.key));
      updated.push({ key: 'textSearch', substring: value, columns: searchableKeys });
    }
    setFilterState(updated);
  };

  // Handle clear all filters
  const handleClearFilters = () => {
    setFilterState([]);
    setSearchValue('');
  };

  // Columns that have a filter dropdown, in column order (so filter order matches column order)
  const getColumnsWithFilterDropdownsInOrder = () => {
    return columns.filter(
      column =>
        (column.filters?.oneOf?.options?.length && column.filters.oneOf.options.length > 0) ||
        column.filters?.date?.enabled ||
        (column.filters?.oneOfORNotOneOf?.options?.length && column.filters.oneOfORNotOneOf.options.length > 0)
    );
  };

  // get column with quick filter
  const columnWithQuickFilter = columns.find(
    column => column.filters?.quickFilter?.options?.length && column.filters.quickFilter.options.length > 0
  );

  const columnFilterState = filterState.find(filter => filter.key === columnWithQuickFilter?.key) || {
    key: (columnWithQuickFilter?.key as string) || ''
  };
  const quickFilter = columnWithQuickFilter?.filters?.quickFilter;

  // Render filters content (reusable for mobile)
  const renderFiltersContent = () => (
    <Layout direction="column" gap={8}>
      {clearFilters && (
        <Layout height={32} align="center" flexShrink={0}>
          <TextButton
            textSize="body-sm"
            text="Clear Filters"
            color="gray-600"
            paddingY={false}
            onClick={handleClearFilters}
            size="sm"
            semibold={false}
            fullWidth
          />
        </Layout>
      )}
      {!disableSearch && (
        <Layout width="100%" position="relative">
          <TextInput
            icon="search"
            styleType="fill"
            label={searchLabelText}
            hideLabel={true}
            size={HEADER_INPUT_SIZE}
            placeholder={searchPlaceholderText}
            value={searchValue}
            onChange={({ value }) => handleSearchChange(value)}
            onClear={() => {
              const updated = filterState.filter(f => f.key !== 'textSearch');
              setFilterState(updated);
              setSearchValue('');
            }}
            clearButtonAlwaysVisibleWhenFilled
          />
        </Layout>
      )}

      {getColumnsWithFilterDropdownsInOrder().map(column => {
        const columnFilterState = filterState.find(filter => filter.key === column.key) || {
          key: column.key as string
        };
        if (column.filters?.oneOf?.options?.length && column.filters.oneOf.options.length > 0) {
          const defaultOption = column.filters?.oneOf?.defaultOption;
          if (!defaultOption) return null;
          return (
            <FilterDropdown
              key={String(column.key)}
              filterState={columnFilterState}
              setFilterState={newFilterState => {
                const updatedFilterState = filterState.filter(f => f.key !== column.key);
                const defaultIsAny = defaultOption.any;
                if (newFilterState.oneOf?.[0] !== defaultOption.anyFilterOption?.value || !defaultIsAny) {
                  updatedFilterState.push(newFilterState);
                }
                setFilterState(updatedFilterState);
              }}
              options={column.filters?.oneOf?.options || []}
              defaultOption={defaultOption}
              column={column}
            />
          );
        }
        if (column.filters?.date?.enabled) {
          return (
            <DateFilter
              key={String(column.key)}
              filterState={columnFilterState}
              column={column}
              setFilterState={newFilterState => {
                const updatedFilterState = filterState.filter(f => f.key !== column.key);
                if (newFilterState !== null) {
                  updatedFilterState.push(newFilterState);
                }
                setFilterState(updatedFilterState);
              }}
            />
          );
        }
        if (column.filters?.oneOfORNotOneOf?.options?.length && column.filters.oneOfORNotOneOf.options.length > 0) {
          return (
            <OneOfOrNotOneOfFilter
              key={String(column.key)}
              filterState={columnFilterState}
              column={column}
              setFilterState={newFilterState => {
                const updatedFilterState = filterState.filter(f => f.key !== column.key);
                if (newFilterState !== null) {
                  updatedFilterState.push(newFilterState);
                }
                setFilterState(updatedFilterState);
              }}
            />
          );
        }
        return null;
      })}
    </Layout>
  );

  if (isMobile) {
    return (
      <>
        <Layout direction="column" position="sticky" top={0} color="white" zIndex={1000} width="100%">
          <Layout paddingX={24} paddingY={12}>
            <button
              onClick={() => setIsMobileFiltersOpen(true)}
              style={{
                width: '100%',
                padding: '12px 12px',
                borderRadius: '8px',
                border: `1px solid ${GRAY_50_HEX}`,
                backgroundColor: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Text size="body-sm" semibold>
                Search and Filter
              </Text>
              <Icon name="downChevron" size="xs" />
            </button>
          </Layout>
        </Layout>
        <Modal
          isVisible={isMobileFiltersOpen}
          onClose={() => setIsMobileFiltersOpen(false)}
          isBottomSheet={true}
          bottomSheetIsRounded
          header="Search and Filter"
          hasBottomSheetPaddingX={true}
          scrollable={true}
          containerRef={modalContainerRef}
          paddingBottom={64}
        >
          <Layout paddingY={16} paddingBottom={400} direction="column" gap={8}>
            {renderFiltersContent()}
            {quickFilter && (
              <Layout>
                <Layout direction="row" height={32} padding={2} color="gray-25" borderRadius={8}>
                  {quickFilter.options.map(option => {
                    const isSelected =
                      option.value === 'All' ? !columnFilterState.oneOf : columnFilterState.oneOf?.[0] === option.value;
                    return (
                      <button
                        key={String(option.value)}
                        className={`${isSelected ? 'bg-color-white' : ''}`}
                        style={{
                          padding: isMobile ? '6px 11px' : '6px 16px',
                          cursor: 'pointer',
                          height: '28px',
                          borderRadius: '6px'
                        }}
                        onClick={() => {
                          const updatedFilterState = filterState.filter(f => f.key !== columnWithQuickFilter.key);
                          if (option.value !== 'All') {
                            updatedFilterState.push({
                              key: String(columnWithQuickFilter.key),
                              oneOf: [String(option.value)]
                            });
                          }
                          setFilterState(updatedFilterState);
                        }}
                      >
                        <Text size="body-xs" semibold>
                          {option.label}
                        </Text>
                      </button>
                    );
                  })}
                </Layout>
              </Layout>
            )}
          </Layout>
        </Modal>
      </>
    );
  }

  return (
    <Layout
      paddingX={isMobile ? undefined : 40}
      paddingY={12}
      direction={isMobile ? 'column' : 'row'}
      justify={quickFilter ? 'space-between' : 'flex-start'}
      gap={12}
      position="sticky"
      top={0}
      color="white"
      zIndex={1000}
      maxWidth={width}
    >
      <Layout direction="row" gap={8} __style={{ flexWrap: 'wrap' }}>
        {!disableSearch && (
          <Layout width={searchInputWidth} position="relative">
            <TextInput
              icon="search"
              styleType="fill"
              label={searchLabelText}
              hideLabel={true}
              size="xxs"
              placeholder={searchPlaceholderText}
              value={searchValue}
              onChange={({ value }) => handleSearchChange(value)}
              onClear={() => {
                const updated = filterState.filter(f => f.key !== 'textSearch');
                setFilterState(updated);
                setSearchValue('');
              }}
              clearButtonAlwaysVisibleWhenFilled
            />
          </Layout>
        )}

        {getColumnsWithFilterDropdownsInOrder().map(column => {
          const columnFilterState = filterState.find(filter => filter.key === column.key) || {
            key: column.key as string
          };
          if (column.filters?.oneOf?.options?.length && column.filters.oneOf.options.length > 0) {
            const defaultOption = column.filters?.oneOf?.defaultOption;
            if (!defaultOption) return null;
            return (
              <FilterDropdown
                key={String(column.key)}
                filterState={columnFilterState}
                setFilterState={newFilterState => {
                  const updatedFilterState = filterState.filter(f => f.key !== column.key);
                  const defaultIsAny = defaultOption.any;
                  if (newFilterState.oneOf?.[0] !== defaultOption.anyFilterOption?.value || !defaultIsAny) {
                    updatedFilterState.push(newFilterState);
                  }
                  setFilterState(updatedFilterState);
                }}
                options={column.filters?.oneOf?.options || []}
                defaultOption={defaultOption}
                column={column}
              />
            );
          }
          if (column.filters?.date?.enabled) {
            return (
              <DateFilter
                key={String(column.key)}
                filterState={columnFilterState}
                column={column}
                setFilterState={newFilterState => {
                  const updatedFilterState = filterState.filter(f => f.key !== column.key);
                  if (newFilterState !== null) {
                    updatedFilterState.push(newFilterState);
                  }
                  setFilterState(updatedFilterState);
                }}
              />
            );
          }
          if (column.filters?.oneOfORNotOneOf?.options?.length && column.filters.oneOfORNotOneOf.options.length > 0) {
            return (
              <OneOfOrNotOneOfFilter
                key={String(column.key)}
                filterState={columnFilterState}
                column={column}
                setFilterState={newFilterState => {
                  const updatedFilterState = filterState.filter(f => f.key !== column.key);
                  if (newFilterState !== null) {
                    updatedFilterState.push(newFilterState);
                  }
                  setFilterState(updatedFilterState);
                }}
              />
            );
          }
          return null;
        })}
      </Layout>
      {clearFilters && (
        <Layout height={32} align="center" flexShrink={0}>
          <TextButton
            textSize="body-xs"
            text="Clear Filters"
            color="gray-600"
            paddingY={false}
            onClick={handleClearFilters}
            size="sm"
            semibold={false}
            fullWidth
          />
        </Layout>
      )}
      {quickFilter && !isMobile && (
        <Layout>
          <Layout direction="row" height={32} padding={2} color="gray-25" borderRadius={8}>
            {quickFilter.options.map(option => {
              const isSelected =
                option.value === 'All' ? !columnFilterState.oneOf : columnFilterState.oneOf?.[0] === option.value;
              return (
                <button
                  key={String(option.value)}
                  className={`${isSelected ? 'bg-color-white' : ''}`}
                  style={{
                    padding: isMobile ? '6px 11px' : '6px 16px',
                    cursor: 'pointer',
                    height: '28px',
                    borderRadius: '6px'
                  }}
                  onClick={() => {
                    const updatedFilterState = filterState.filter(f => f.key !== columnWithQuickFilter.key);
                    if (option.value !== 'All') {
                      updatedFilterState.push({
                        key: String(columnWithQuickFilter.key),
                        oneOf: [String(option.value)]
                      });
                    }
                    setFilterState(updatedFilterState);
                  }}
                >
                  <Text size="body-xs" semibold>
                    {option.label}
                  </Text>
                </button>
              );
            })}
          </Layout>
        </Layout>
      )}
    </Layout>
  );
}

export default TableControlsHeader;
